import { Table } from "antd";
import type { TableProps } from "antd/es/table";
import type { Key } from "antd/es/table/interface";
import type { ColumnType } from "antd/lib/table/interface";
import { useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";

import type { Entity } from "../../../core/types/fe";
import type { ExtendedColumnsType } from "./Table";

export interface SelectableTableProps<T> {
    getColumnsConfig: (t: Function) => ExtendedColumnsType<T>[];
    propertyAsRowKey: keyof T;
    tableData: T[];
    dataSource: T[];
    onDataChanged: Function;
    onRow?: TableProps<T>["onRow"];
}

export type DataItemWithKey<T> = T & { key: T[keyof T] };

export const SelectableTable = <T extends Entity>({
    getColumnsConfig,
    propertyAsRowKey,
    tableData = [],
    dataSource,
    onDataChanged,
    onRow,
}: SelectableTableProps<T>) => {
    const { t } = useTranslation();
    const columns = useMemo(() => getColumnsConfig(t), [t, getColumnsConfig]);

    const columnConfig = extendColumnConfig(columns);

    const [selectedRowKeys, setSelectedRowKeys] = useState<Key[]>([]);

    const onSelectChange = (newSelectedRowKeys: Key[]) => {
        setSelectedRowKeys(newSelectedRowKeys);
        const newValues = dataSource.filter((terminal) => newSelectedRowKeys.some((id) => terminal.id === id));
        if (newValues) {
            onDataChanged(newValues);
        }
    };

    useEffect(() => {
        if (tableData) {
            const idArray: Key[] = tableData.map((terminal) => terminal[propertyAsRowKey] as Key);
            setSelectedRowKeys(idArray);
        }
    }, [propertyAsRowKey, tableData]);

    const rowSelection = {
        selectedRowKeys,
        onChange: onSelectChange,
    };

    return (
        <Table<T>
            rowSelection={rowSelection}
            columns={columnConfig}
            dataSource={addKeyProp<T>(dataSource as Readonly<T[]>, propertyAsRowKey)}
            onRow={onRow}
            pagination={false}
        />
    );
};

function extendColumnConfig<T>(columns: ExtendedColumnsType<T>[]): ColumnType<T>[] {
    return columns as ColumnType<T>[];
}

// antd table requires to have a key prop in the table's row object, otherwise
// it will log warning in the console
function addKeyProp<T>(array: ReadonlyArray<T>, propertyAsRowKey: keyof T) {
    return array?.map((obj) => {
        return { ...obj, key: obj[propertyAsRowKey] } as DataItemWithKey<T>;
    });
}
